// Shared questionnaire answer transforms — the single implementation behind
// (a) the clinician manual-entry save path, (b) the server-side questionnaire
// chart overlay, and (c) call-answer ingest. Keeping the transform semantics
// in contracts guarantees a value normalizes identically no matter which
// surface captured it.

import { parseYesNoUtteranceV1 } from "./answer-normalization.js";
import type { ClinicianWorkbenchQuestionnaireFieldBinding } from "./clinician-workbench-contract.js";

export interface IsoDatePartsV1 {
  year: number;
  monthIndex: number;
  day: number;
  /** Canonical zero-padded YYYY-MM-DD — the single serialization used for
   *  calendar-cell comparison and persisted date answers. */
  isoDate: string;
}

/** Strict YYYY-MM-DD (or YYYY-M-D) with real-calendar validation — "2026-02-30"
 *  must not silently roll into March when it seeds a picker month. */
export function parseIsoDateTextV1(text: string): IsoDatePartsV1 | null {
  const match = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/.exec(text);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, monthIndex, day));

  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== monthIndex ||
    candidate.getUTCDate() !== day
  ) {
    return null;
  }

  const isoDate = [
    String(year).padStart(4, "0"),
    String(monthIndex + 1).padStart(2, "0"),
    String(day).padStart(2, "0"),
  ].join("-");

  return { year, monthIndex, day, isoDate };
}

export function normalizePhoneInputV1(text: string): string | null {
  const compact = text.replace(/[\s().\-]/gu, "");

  if (compact.startsWith("+")) {
    const digits = compact.slice(1);

    if (/^\d{11,15}$/.test(digits)) {
      return `+${digits}`;
    }

    return null;
  }

  if (/^\d{10}$/.test(compact)) {
    return `+1${compact}`;
  }

  if (/^1\d{10}$/.test(compact)) {
    return `+${compact}`;
  }

  return null;
}

/** Apply a binding transform to a raw answer. Returns null/undefined when the
 *  value cannot be interpreted — callers must withhold such answers (writing
 *  the null would validate as a deliberate "clear" and lose the answer). */
export function transformQuestionnaireAnswerV1(
  value: unknown,
  transform: ClinicianWorkbenchQuestionnaireFieldBinding["transform"],
): unknown {
  switch (transform) {
    case "yesNoToBoolean":
    case "yesNo": {
      if (typeof value === "boolean") return value;
      return parseYesNoUtteranceV1(value);
    }
    case "toNumber": {
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      const text = String(value ?? "").trim().toLowerCase();
      // Spoken heights arrive as feet+inches far more often than raw inches.
      const feetInches = text.match(/(\d+)\s*(?:'|’|ft\b|feet\b|foot\b)\s*(\d+)?/);
      if (feetInches) {
        return Number(feetInches[1]) * 12 + Number(feetInches[2] ?? 0);
      }
      const digits = text.replace(/[^0-9.\-]/g, "");
      // Number("") is 0 — prose like "not sure" must not become a measurement.
      if (!/[0-9]/.test(digits)) return null;
      const parsed = Number(digits);
      return Number.isFinite(parsed) ? parsed : null;
    }
    case "phoneNormalize": {
      return normalizePhoneInputV1(String(value ?? "")) ?? null;
    }
    case "list":
      return Array.isArray(value) ? value : value == null ? [] : [value];
    case "text": {
      if (value == null) return null;
      return Array.isArray(value) ? value.map(String).join(", ") : String(value);
    }
    case "dateNormalize":
    case "date": {
      // Canonical zero-padded ISO or nothing: returning null routes prose
      // like "next tuesday" into unparsedAnswers instead of persisting a
      // string later structured consumers cannot read.
      return parseIsoDateTextV1(String(value ?? ""))?.isoDate ?? null;
    }
    case "verbatim":
    default:
      return value;
  }
}
